import { useEffect, useState } from 'react'
import { MIN_ELEMENT_SIZE } from 'editor'
import { useTranslation } from 'libs/i18n'
import { ScalingIcon } from 'shared/atoms'
import type { FloormapConfig } from 'features/editor'

type SizeKey = 'width' | 'height' | 'radius'

type SelectedAreaProps = {
	element: FloormapConfig[number]
	floorSize: { width: number; height: number }
	onChange: (key: SizeKey, value: number) => void
}

type SizeFieldProps = {
	label: string
	value: number
	min: number
	max: number
	onChange: (value: number) => void
}

const SizeField = ({ label, value, min, max, onChange }: SizeFieldProps) => {
	const [draft, setDraft] = useState(String(value))

	useEffect(() => setDraft(String(value)), [value])

	return (
		<label className="block space-y-1">
			<span className="text-[11px] font-medium text-muted-foreground">{label}</span>
			<input
				type="number"
				min={min}
				max={max}
				value={draft}
				className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
				onChange={(event) => {
					const next = event.target.valueAsNumber

					setDraft(event.target.value)
					if (next >= min) onChange(Math.min(Math.round(next), max))
				}}
				onBlur={() => setDraft(String(value))}
			/>
		</label>
	)
}

export const SelectedArea = ({ element, floorSize, onChange }: SelectedAreaProps) => {
	const { t } = useTranslation()

	if (!('x' in element)) return null

	const label =
		element.type === 'booth' ? element.id : element.type === 'rect' ? element.label : undefined

	const renderFields = () => {
		if (element.type === 'circle') {
			return (
				<SizeField
					label={t('Radius')}
					value={element.radius}
					min={MIN_ELEMENT_SIZE / 2}
					max={Math.floor(
						Math.min(
							element.x,
							element.y,
							floorSize.width - element.x,
							floorSize.height - element.y
						)
					)}
					onChange={(value) => onChange('radius', value)}
				/>
			)
		}

		if (!('width' in element)) return null

		return (
			<>
				<SizeField
					label={t('Width')}
					value={element.width}
					min={MIN_ELEMENT_SIZE}
					max={Math.floor(floorSize.width - element.x)}
					onChange={(value) => onChange('width', value)}
				/>
				<SizeField
					label={t('Height')}
					value={element.height}
					min={MIN_ELEMENT_SIZE}
					max={Math.floor(floorSize.height - element.y)}
					onChange={(value) => onChange('height', value)}
				/>
			</>
		)
	}

	const fields = renderFields()

	if (!fields) return null

	return (
		<div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-3">
			<p className="flex items-center gap-2 text-xs font-semibold text-foreground">
				<ScalingIcon className="h-3.5 w-3.5 shrink-0" />
				{label ? t('Area {{ label }}', { label }) : t('Selected area')}
			</p>
			<div className="mt-2 grid grid-cols-2 gap-2">{fields}</div>
			<p className="mt-2 text-[11px] text-muted-foreground">
				{t('Or drag the corner handle on the map.')}
			</p>
		</div>
	)
}
